import { useLazyQuery } from '@apollo/client';
import { debounce, throttle } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { AuthActions } from 'common/auth/actions';
import { GetRoleAndSubscriberDocumentNode } from 'common/auth/queries';
import type { AuthState } from 'common/auth/types';
import { featureFlags } from 'common/featureFlags/featureFlags';
import { AuthUserType } from 'common/graphql/types';
import { useOnVisibilityChange } from 'common/hooks/useOnVisibilityChange';
import { CIPageStatus } from 'common/viewcomp/general/ci_pagestatus';

const THROTTLE_MS = 10000;
const DEBOUNCE_MS = 12000;

export const useAutoRoleSync = () => {
    const store = useStore<{ auth: AuthState }>();

    const checkIfRoleChangedOnBE = useCheckIfRoleChangedOnBE();

    const debouncedCheckIfRoleChangedOnBe = useCallback(debounce(checkIfRoleChangedOnBE, DEBOUNCE_MS), [
        checkIfRoleChangedOnBE,
    ]);

    const throttledCheckIfRoleChangedOnBe = useCallback(
        throttle(
            () => {
                // call the function one more time in few seconds
                // for when the user comes back to the page too quickly from FX
                debouncedCheckIfRoleChangedOnBe();

                checkIfRoleChangedOnBE();
            },
            THROTTLE_MS,
            { trailing: true }
        ),
        [checkIfRoleChangedOnBE, debouncedCheckIfRoleChangedOnBe]
    );

    const checkIfRoleChangedOnBeWithChecks = useCallback(() => {
        const { activeUser, submitted, switchingUser, userRoleSwitchPending } = store.getState()?.auth || {};

        const shouldNotRun =
            !featureFlags.fxIntegration ||
            !activeUser ||
            activeUser.type === AuthUserType.PATIENT ||
            submitted ||
            document.visibilityState !== 'visible' ||
            activeUser.authenticationMethod !== 'OKTA' ||
            switchingUser ||
            userRoleSwitchPending;

        if (shouldNotRun) {
            return;
        }

        throttledCheckIfRoleChangedOnBe();
    }, [store, throttledCheckIfRoleChangedOnBe, featureFlags.fxIntegration]);

    useRunIfActiveUserInitiated(checkIfRoleChangedOnBeWithChecks);
    useOnVisibilityChange(checkIfRoleChangedOnBeWithChecks);
};

const useRunIfActiveUserInitiated = (checkIfRoleChangedOnBeWithChecks: () => void) => {
    const activeUser = useSelector((state: { auth: AuthState }) => state.auth.activeUser);

    useEffect(() => {
        if (activeUser) {
            checkIfRoleChangedOnBeWithChecks();
        }
    }, [activeUser, checkIfRoleChangedOnBeWithChecks]);
};

const useCheckIfRoleChangedOnBE = () => {
    const store = useStore<{ auth: AuthState }>();

    const { t } = useTranslation('auth', { useSuspense: false });
    const dispatch = useDispatch();
    const [getRoleInfo] = useLazyQuery(GetRoleAndSubscriberDocumentNode);

    const resetUserData = useCallback(() => {
        dispatch(AuthActions.resetUserData());
    }, [t, dispatch]);

    const checkIfRoleChangedOnBE = useCallback(async () => {
        const activeUser = store.getState()?.auth?.activeUser;

        if (!activeUser) {
            return;
        }

        const { data } = await getRoleInfo();

        const userRoleSwitchPending = store.getState()?.auth?.userRoleSwitchPending;

        if (userRoleSwitchPending || !data?.getUser) {
            return;
        }

        const { subscriberId } = data.getUser.currentUserSubscriber;
        const subscriberChanged = activeUser.subscriberPk !== subscriberId;

        if (subscriberChanged) {
            CIPageStatus.warn(t('switch_role.warning.switching_subscriber'));
            resetUserData();

            return;
        }

        const roleVClaim = store.getState()?.auth?.activeUser?.roleVClaim;
        const roleBClaim = store.getState()?.auth?.activeUser?.roleBClaim;

        const stateRoleClaimSk = roleVClaim?.sk || roleBClaim?.sk;
        const stateRoleClaimFxId = roleVClaim?.fxId || roleBClaim?.fxId;

        const backendRoleClaimSk = data.getUser.roleVClaim?.sk || data.getUser.roleBClaim?.sk;
        const backendRoleClaimFxId = data.getUser.roleVClaim?.fxId || data.getUser.roleBClaim?.fxId;

        const backendRoleChanged =
            stateRoleClaimSk !== backendRoleClaimSk ||
            (stateRoleClaimFxId && stateRoleClaimFxId !== backendRoleClaimFxId);

        if (backendRoleChanged) {
            CIPageStatus.warn(t('switch_role.warning.switching_role'));
            resetUserData();

            return;
        }
    }, [store, getRoleInfo, featureFlags.fxIntegration, resetUserData]);

    return checkIfRoleChangedOnBE;
};
